import api from './client';
import { API_ENDPOINTS } from './endpoints';

export type ApprovalAction = 'approved' | 'rejected';
export type ApprovalStage = 'qc' | 'reporting_sme' | 'atl' | 'reporting_lead' | 'reporting_manager' | 'senior_manager' | 'ceo';
// 'focused' = the approver opened this ticket's details/snapshot before approving
// (enforced client-side); 'standard' = the plain Approve / Bulk Approve path.
export type ApprovalReviewMode = 'standard' | 'focused';

export interface RejectionHistoryEntry {
  stage: ApprovalStage;
  remarks?: string;
  rejected_by_id?: string;
  rejected_by_name?: string;
  rejected_at?: string;
}

export type StageHistoryAction = 'approved' | 'rejected' | 'rolled_back';

export interface StageHistoryEntry {
  stage: ApprovalStage;
  action: StageHistoryAction;
  by_id?: string;
  by_name?: string;
  at?: string;
  remarks?: string;
  // Only meaningful when action === 'approved'.
  review_mode?: ApprovalReviewMode;
}

// QC, Reporting SME, and Reporting Lead all review in parallel — whichever of
// them acts first finalizes the approval. Resolved once at creation from the
// ticket owner's qc_id/reporting_sme_id/reporting_lead_id.
export interface ParallelApproverEntry {
  stage: 'qc' | 'reporting_sme' | 'reporting_lead';
  user_id?: string;
  user_name?: string;
  user_email?: string;
}

export interface ApprovalApiItem {
  _id: string;
  ticket_id?: string;
  custom_ticket_id?: string;
  claim_number?: string;
  department_name?: string;
  client_name?: string;
  insurance_party_name?: string;
  payer_name?: string;
  account_no?: string;
  ticket_status_snapshot?: string;
  submission_status?: string;
  resubmitted_count?: number;
  follow_up_snapshot?: {
    basic?: {
      follow_up_date?: string | null;
      next_follow_up_date?: string | null;
      resolution_date?: string | null;
      assigned_to_name?: string;
      policy_member_id?: string;
      provider_practice_name?: string;
      description?: string;
      key_points?: string;
    };
    financial?: {
      billed_amount?: number;
      insurance_paid_amount?: number;
      write_off_amount?: number;
      [key: string]: number | undefined;
    };
    call_log?: {
      call_date?: string | null;
      call_start_time?: string;
      call_end_time?: string;
      call_duration?: string;
      call_reference_number?: string;
      call_type?: string;
    };
    accounts_receivable?: {
      account_no?: string;
    };
  };
  requested_by_id?: string;
  requested_by_name?: string;
  approver_user_id?: string;
  approver_user_name?: string;
  approver_user_email?: string;
  parallel_approvers?: ParallelApproverEntry[];
  stage?: ApprovalStage;
  action_status?: 'pending' | 'approved' | 'rejected';
  // A rolled-back approval's action_status stays "pending" (it's just back at an
  // earlier stage) — this is the real signal that it's currently in that state,
  // as opposed to never having moved yet. Cleared by any later approve/reject.
  is_rolled_back?: boolean;
  // The most recent approve action's mode — once the chain finalizes (action_status
  // becomes 'approved'), this is the finalizing stage's choice.
  review_mode?: ApprovalReviewMode;
  action_remarks?: string;
  lead_approved_by_id?: string;
  lead_approved_by_name?: string;
  lead_approved_at?: string;
  qc_approved_by_id?: string;
  qc_approved_by_name?: string;
  qc_approved_at?: string;
  manager_approved_by_id?: string;
  manager_approved_by_name?: string;
  manager_approved_at?: string;
  rejected_stage?: ApprovalStage | null;
  rejection_history?: RejectionHistoryEntry[];
  stage_history?: StageHistoryEntry[];
  requested_at?: string;
  custom_approval_id?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface ApprovalsListResponse {
  success: boolean;
  statusCode: number;
  message: string;
  data?: {
    approvals?: ApprovalApiItem[];
    items?: ApprovalApiItem[];
    records?: ApprovalApiItem[];
    count?: number;
    total?: number;
  } | ApprovalApiItem[];
}

export interface TakeApprovalActionPayload {
  action: ApprovalAction;
  remarks?: string | null;
  // Only meaningful when action is 'approved'; omitted/ignored for 'rejected'.
  // Defaults server-side to 'standard' when not sent.
  review_mode?: ApprovalReviewMode;
  // Optional Reporting-Lead-only department hand-off — meaningful only when
  // action is 'approved' and the approval's current stage is reporting_lead.
  // target_reporting_lead_ids is the required part of a hand-off (who it goes
  // to); target_user_ids is an optional further narrowing to specific people
  // under them. assign_all is a legacy "all users in the department" mode,
  // superseded by target_reporting_lead_ids but still honored server-side.
  target_department_name?: string;
  target_reporting_lead_ids?: string[];
  target_user_ids?: string[];
  assign_all?: boolean;
}

export interface TakeApprovalActionResponse {
  success: boolean;
  statusCode: number;
  message: string;
  data?: unknown;
}

export interface GetApprovalsParams {
  [key: string]: string | number | boolean | undefined | null;
  page?: number;
  limit?: number;
  search?: string;
  // 'rolled_back' is a virtual status the backend resolves to
  // { action_status: 'pending', is_rolled_back: true } — see approvals.controller.js.
  action_status?: 'pending' | 'approved' | 'rejected' | 'rolled_back';
  'meta.status'?: 'active' | 'inactive';
  requested_by_name?: string;
}

export interface RollbackApprovalPayload {
  remarks: string;
}

export interface RollbackApprovalResponse {
  success: boolean;
  statusCode: number;
  message: string;
  data?: unknown;
}

export interface DistinctRequesterNamesResponse {
  success: boolean;
  statusCode: number;
  message: string;
  data?: {
    requesterNames?: string[];
  };
}

// Scoped to the current user — visible if it's awaiting them now, or they
// acted on it at any earlier stage.
export const getApprovals = async (params?: GetApprovalsParams): Promise<ApprovalsListResponse> => {
  return api.get<ApprovalsListResponse>(API_ENDPOINTS.approvals.list, { params });
};

// Unrestricted — every approval regardless of who it's tied to, for any role.
export const getAllApprovals = async (params?: GetApprovalsParams): Promise<ApprovalsListResponse> => {
  return api.get<ApprovalsListResponse>(API_ENDPOINTS.approvals.all, { params });
};

export const takeApprovalAction = async (
  approvalId: string,
  payload: TakeApprovalActionPayload,
): Promise<TakeApprovalActionResponse> => {
  return api.post<TakeApprovalActionResponse, TakeApprovalActionPayload>(
    `${API_ENDPOINTS.approvals.list}/${approvalId}/action`,
    payload,
  );
};

export interface TakeBulkApprovalActionPayload {
  ids: string[];
  action: ApprovalAction;
  remarks?: string | null;
  // Applies to the whole batch — only meaningful when action is 'approved'.
  review_mode?: ApprovalReviewMode;
}

export interface TakeBulkApprovalActionResponse {
  success: boolean;
  statusCode: number;
  message: string;
  data?: {
    succeeded_count: number;
    failed: { id: string; reason: string }[];
  };
}

// "Approve All" / "Reject All" — same per-approval rules as takeApprovalAction,
// but the backend groups every notification email by recipient and sends at most
// one (a single tabular summary when more than one ticket lands on the same
// person) instead of one email per ticket.
export const takeBulkApprovalAction = async (
  payload: TakeBulkApprovalActionPayload,
): Promise<TakeBulkApprovalActionResponse> => {
  return api.post<TakeBulkApprovalActionResponse, TakeBulkApprovalActionPayload>(
    API_ENDPOINTS.approvals.bulkAction,
    payload,
  );
};

// Moves a currently-pending approval back one stage (the nearest earlier stage
// with a live active approver), reopening it as pending there.
export const rollbackApproval = async (
  approvalId: string,
  payload: RollbackApprovalPayload,
): Promise<RollbackApprovalResponse> => {
  return api.post<RollbackApprovalResponse, RollbackApprovalPayload>(
    `${API_ENDPOINTS.approvals.list}/${approvalId}/rollback`,
    payload,
  );
};

// Distinct requester names for the "filter by requester" dropdown — scoped to
// what the current user can see unless scope='all' (and they're privileged).
export const getDistinctRequesterNames = async (
  scope?: 'all' | 'mine',
): Promise<DistinctRequesterNamesResponse> => {
  return api.get<DistinctRequesterNamesResponse>(API_ENDPOINTS.approvals.requesterNames, {
    params: scope ? { scope } : undefined,
  });
};
